/**
 * Help Center: "confirmation-and-travel" section articles. Format and rules: src/content/help/README.md.
 */
export default [
  {
    slug: 'when-confirmation-number',
    title: 'When do I get my confirmation number?',
    summary: 'You get your airline confirmation number once your trip is paid in full, usually within 24 hours on weekdays. Here’s how it works and what to expect.',
    keywords: ['airline confirmation', 'PNR', 'record locator', 'confirmation code', 'paid in full', 'release', 'airline reference'],
    related: [
      'confirmation-and-travel/paid-off-no-confirmation',
      'confirmation-and-travel/price-protection-and-confirmation',
      'payments/pay-off-early',
      'confirmation-and-travel/use-confirmation-with-airline',
    ],
    updated: '2026-10-05',
    body: `You'll get your airline confirmation number once your trip is paid in full. After your final payment goes through, our team releases it to you, usually within 24 hours on weekdays.

Until then, it's completely normal for your trip to have a payment schedule and no confirmation number yet. Nothing is wrong with your booking, and your trip price is already protected.

## What a confirmation number is

Your confirmation number is the code the airline uses to find your reservation. It's usually a short mix of letters and numbers, and some airlines call it a record locator. You'll use it to check in for your flight, and to look up or manage your trip on the airline's website or app.

It isn't the same as your Defera booking reference, which is the code at the top of your booking confirmation email. Your booking reference is how we find your trip at Defera, but it isn't something the airline can look up.

## How the release works

You don't need to do anything to get the process started. Here's what happens:

1. **You make your final payment.** As soon as it goes through, a "Confirmation Number Release" request is sent to our team for you automatically. You'll see it as a card on your trip page.
2. **Our team releases your confirmation number.** They work Monday through Friday, Pacific time.
3. **We email you.** When your confirmation number is ready, you'll get a "Your trip has been paid off!" email. Tap **Get Confirmation Number** in the email to see it, or open **Trips** and tap your trip.
4. **The airline emails you too.** You'll also hear from the airline directly, at the email address on your booking.

## How long it takes

It depends on when your final payment goes through:

- **Monday through Thursday, or Friday morning (Pacific time):** expect your confirmation number within 24 hours.
- **Friday afternoon or over the weekend:** expect it on Monday, or Tuesday at the latest.

For example, if your last payment goes through on a Wednesday morning, you can expect your confirmation number by Thursday morning. If it goes through on a Saturday, look for it on Monday.

Sometimes our team schedules a release for a specific date. If yours has been scheduled, your trip page shows that date, and that's the day to expect your confirmation number.

## What if it's taking longer?

If it's been longer than that, or you don't see the release card on your trip page, open your trip in **Trips** and tap **Finished paying for your trip?** in the payment schedule. It sends the request if one wasn't sent, or confirms that yours is already being handled.

If you're still waiting after that, [chat with the Defera Concierge](/support), our AI concierge. It can check on your trip and send a request to our team. [I've paid off my trip. Where's my confirmation number?](/help/confirmation-and-travel/paid-off-no-confirmation) walks through this step by step.

## Can I get it sooner?

Yes. Because your confirmation number comes once your trip is paid in full, paying off your remaining balance early gets it to you sooner, and there's no penalty for paying early. Open your trip in **Trips**, tap **Make a Payment**, and choose **Pay off**. [Paying off your trip early](/help/payments/pay-off-early) explains how it works.

## Your trip price is already protected

Waiting for your confirmation number doesn't put your price at risk. Your trip price was secured when your booking was approved, and it stays protected as long as you keep up with your payment plan, no matter when your confirmation number arrives. [Price protection vs. your confirmation number](/help/confirmation-and-travel/price-protection-and-confirmation) explains how the two fit together.`,
  },
  {
    slug: 'paid-off-no-confirmation',
    title: 'I’ve paid off my trip. Where’s my confirmation number?',
    summary: 'It’s on its way. Your final payment sends a release request to our team automatically, and your confirmation number usually follows within 24 hours on weekdays.',
    keywords: ['paid in full', 'final payment', 'release request', 'finished paying', 'still waiting', 'missing confirmation', 'PNR'],
    related: [
      'confirmation-and-travel/confirmation-taking-longer',
      'confirmation-and-travel/when-confirmation-number',
      'emails/paid-off-email',
      'contact/check-request-status',
    ],
    updated: '2026-10-05',
    body: `Congratulations on paying off your trip. Your confirmation number is on its way. Your final payment sent a release request to our team automatically, and they usually release it within 24 hours on weekdays.

If your final payment went through recently, there's nothing more you need to do. It's normal to see the release request on your trip, and no confirmation number yet, for a little while.

## Where to look

Open **Trips** and tap your trip. On your trip page, you'll see a "Confirmation Number Release" card. That's your release request, sent for you automatically when your final payment went through.

While our team works on it, the card shows "Submitted · in progress". Once your confirmation number is released, the card updates with the answer, and your confirmation number appears on your trip page.

## How long it takes

Our team releases confirmation numbers Monday through Friday, Pacific time, so the timing depends on when your final payment goes through:

- **Monday through Thursday, or Friday morning (Pacific time):** expect your confirmation number within 24 hours.
- **Friday afternoon or over the weekend:** expect it on Monday, or Tuesday at the latest.

For example, if your final payment goes through on a Tuesday afternoon, you can expect your confirmation number by Wednesday afternoon. If it goes through on a Friday afternoon, look for it on Monday, or Tuesday at the latest.

## What if it's taking longer?

If it's been longer than that, or you don't see the release card on your trip page, open your trip in **Trips** and tap **Finished paying for your trip?** in the payment schedule. It sends the request if one wasn't sent, or confirms that yours is already being handled.

If you're still waiting after that, [chat with the Defera Concierge](/support), our AI concierge. It can check on your trip and send a request to our team. [My confirmation number is taking longer than expected](/help/confirmation-and-travel/confirmation-taking-longer) covers what to check and what to include.

## What happens when it's released

Here's what you'll see:

1. **Your trip page updates.** Your confirmation number appears on your trip page, and the release card shows the answer.
2. **We email you.** You'll get a "Your trip has been paid off!" email. It comes when your confirmation number is released, not the moment your final payment goes through. Tap **Get Confirmation Number** in the email to see your number. If your trip earned Defera Miles, the email tells you how many.
3. **The airline emails you too.** You'll also hear from the airline directly, at the email address on your booking.
4. **Your Trip Statement shows it.** From then on, your Trip Statement shows your airline confirmation number in place of your Defera booking reference.

## Is the code in my booking email my confirmation number?

No. The code at the top of your booking confirmation email is your Defera booking reference. It's how we find your trip at Defera, but it isn't something the airline can look up.

Your airline confirmation number is a different code, usually a short mix of letters and numbers. It's the one released to your trip page once your trip is paid in full.

## Booked with Defera Miles alone?

If your Defera Miles covered your whole trip total, your trip had no payment plan, so there was no final payment to make. Our team releases your confirmation number as it does for any paid-off trip.

> [!TIP] Ready to fly? [Using your confirmation number with the airline](/help/confirmation-and-travel/use-confirmation-with-airline) explains how to check in and find your trip with the airline.`,
  },
  {
    slug: 'confirmation-taking-longer',
    title: 'My confirmation number is taking longer than expected',
    summary: 'Paid off on a Friday afternoon or over the weekend? Expect your confirmation number Monday, or Tuesday at the latest. Later than that? Here’s what to do.',
    keywords: ['late', 'delayed', 'still waiting', 'weekend', 'release request', 'not received', 'how long'],
    related: [
      'confirmation-and-travel/paid-off-no-confirmation',
      'contact/urgent-help-before-a-flight',
      'contact/how-requests-work',
      'payments/payment-not-showing',
    ],
    updated: '2026-10-23',
    body: `If your confirmation number hasn't arrived yet, it may still be on time. Our team releases confirmation numbers Monday through Friday, Pacific time. So if your final payment went through on a Friday afternoon or over the weekend, your confirmation number comes on Monday, or Tuesday at the latest.

We know waiting can be stressful, especially with a trip coming up. The checks below only take a few minutes. They'll tell you whether your confirmation number is still on its way, or whether it's time to contact us.

## Is it actually late?

It depends on when your final payment went through:

- **Monday through Thursday, or Friday morning (Pacific time):** expect your confirmation number within 24 hours.
- **Friday afternoon or over the weekend:** expect it on Monday, or Tuesday at the latest.

For example, if your final payment went through on a Saturday, look for your confirmation number on Monday, or Tuesday at the latest. If Tuesday has come and gone, it's taking longer than it should, and the steps below will help.

## What to check first

Work through these in order:

1. **Make sure your trip is paid in full.** Open **Trips**, tap your trip and check your payment schedule. The release request is sent when your final payment goes through, so if any balance is left, pay it first with **Make a Payment**.
2. **Look for the release card.** On your trip page, look for a "Confirmation Number Release" card. While our team works on it, it shows "Submitted · in progress".
3. **Send the request yourself if you need to.** If it's past the times above, or there's no card, tap **Finished paying for your trip?** in your trip's payment schedule. It sends the request if one wasn't sent, or confirms that yours is already being handled.

## Still no confirmation number?

[Chat with the Defera Concierge](/support), our AI concierge. It can check on your trip and send a request to our team. To give our team what it needs from the start, include:

- your Defera booking reference, the code at the top of your booking confirmation email
- the date your final payment went through
- your departure date, if you're flying soon

The request shows as a card on your trip page. When our team answers, the answer appears on that card, and it comes to you by email too.

## Other ways to reach us

If you'd rather talk than type, call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}), 24/7. You'll reach the same Defera Concierge by voice, so use whichever you prefer. You can also send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.

> [!IMPORTANT] Flying soon? Tell the concierge when you fly. [Getting urgent help before your flight](/help/contact/urgent-help-before-a-flight) explains how to reach us when time is short.`,
  },
  {
    slug: 'price-protection-and-confirmation',
    title: 'Price protection vs. your confirmation number',
    summary: 'They’re two different things. Your trip price is protected once your booking is approved. Your confirmation number comes later, once you’ve paid in full.',
    keywords: ['price lock', 'locked price', 'fare increase', 'airfare going up', 'good standing', 'booking approved', 'PNR'],
    related: [
      'getting-started/price-protection',
      'missed-payments/good-standing',
      'confirmation-and-travel/when-confirmation-number',
      'payments/pay-off-early',
    ],
    updated: '2026-10-05',
    body: `Price protection and your confirmation number are two different things, and they arrive at different times. Your trip price is protected as soon as your booking is approved. Your confirmation number comes later, once your trip is paid in full.

So if your trip has a payment schedule and no confirmation number yet, that's completely normal. Your price is already protected, and that protection doesn't wait for your confirmation number.

## What price protection means

Price protection means the trip price you agreed to at checkout is the price you pay. Once your booking is approved, your agreed-upon trip price is secured with Defera, and it's protected from future airfare increases.

For example, if fares on your route go up a month after you book, your trip price doesn't go up with them, as long as you keep up with your payment plan. At checkout, you'll see "Price protected from today" above your total.

## What your confirmation number is

Your confirmation number is the code the airline uses to find your reservation. You'll use it to check in, and to look up your trip on the airline's website or app. It's released to your trip page once your trip is paid in full.

It isn't the same as your Defera booking reference, which is the code at the top of your booking confirmation email.

## How the two compare

Here's how they differ, side by side:

| | Price protection | Confirmation number |
|---|---|---|
| What it does | Protects your agreed-upon trip price from future airfare increases | Lets you check in and find your trip with the airline |
| When you get it | Once your booking is approved | Once your trip is paid in full |
| What it needs from you | Staying in good standing on your payment plan | Paying your trip in full |

In short, your price is protected from the start, and your confirmation number comes at the end of your payment plan. Releasing your confirmation number once your trip is paid in full is part of what lets us offer payment plans with no credit check.

## When your price is secured

It depends on your plan:

- **Defera Installments:** your deposit secures your agreed-upon trip price.
- **Defera Zero:** your price is secured with nothing down.

Either way, your price protection starts once your booking is approved.

## How do I keep my price protected?

Your price stays protected while you're in good standing on your payment plan. Being in good standing means keeping up with your payment plan. [What "good standing" means](/help/missed-payments/good-standing) explains it in more detail.

## What if a payment is going to be hard?

If an upcoming payment worries you, you have options, and you can look at them before the payment is due. [I can't make my next payment. What are my options?](/help/missed-payments/cant-pay-on-time) walks through what you can do.

## Can I get my confirmation number sooner?

Yes. Because your confirmation number comes once your trip is paid in full, paying off your remaining balance early gets it to you sooner, and there's no penalty for paying early. Open your trip in **Trips**, tap **Make a Payment**, and choose **Pay off**. [When do I get my confirmation number?](/help/confirmation-and-travel/when-confirmation-number) explains the timing.

## Where can I read the full terms?

For the full legal wording on both price protection and your confirmation number, see the [Terms of Service](/terms).`,
  },
  {
    slug: 'use-confirmation-with-airline',
    title: 'Using your confirmation number with the airline',
    summary: 'Once it’s released, your confirmation number is how the airline finds your trip. Use it to check in, and make date or time changes directly with the airline.',
    keywords: ['airline website', 'airline app', 'manage booking', 'look up reservation', 'record locator', 'codeshare', 'operated by', 'PNR'],
    related: [
      'confirmation-and-travel/check-in',
      'confirmation-and-travel/airline-app-shows-nothing',
      'confirmation-and-travel/airline-says-contact-third-party',
      'booking/codeshare-flights',
    ],
    updated: '2026-10-05',
    body: `Once your trip is paid in full, your airline confirmation number appears on your trip page in **Trips**. It's the code the airline uses to find your reservation, so you'll use it to check in and to look up your trip on the airline's app or website.

From then on, date and time changes are made directly with the airline, under its own fare rules, and so are cancellations. There are still a few things you can do in Defera too, like adding seats or bags.

## How do I find my trip with the airline?

Here's how to look it up:

1. **Find your confirmation number.** Open **Trips** and tap your trip. Your confirmation number is on your trip page.
2. **Check which airline flies each flight.** If a flight shows "Operated by" another airline, a partner airline flies it, and that airline's app or website is usually where your trip lives.
3. **Look up your trip.** On the airline's app or website, look for the option to find or manage a booking. Most airlines ask for your confirmation number and the traveler's last name.

Use your airline confirmation number here, not your Defera booking reference. Your booking reference is the code at the top of your booking confirmation email. It's how we find your trip at Defera, but the airline can't look it up.

## What does "Operated by" mean?

Airlines often sell seats on flights that a partner airline flies. Your itinerary shows this as "Operated by" and the partner airline's name. The operating airline flies the plane and runs check-in, and it usually sets the baggage rules and seat map.

It's completely normal, and it doesn't mean anything is wrong with your booking. [What is a codeshare flight?](/help/booking/codeshare-flights) explains more.

## Why does my confirmation number have two codes?

If your trip uses more than one airline, your confirmation number may show two codes separated by a slash. There's one code for each airline, in the order you fly:

- **The first code** is for the first airline you fly.
- **The next code** is for the airline that operates your following flight or your return.

Use each code with its own airline. For example, if you fly out on one airline and home on another, use the first code with the airline that flies you out, and the second with the airline that flies you home.

## How do I change or cancel my flights?

Once your confirmation number is released, date and time changes are made directly with the airline, using your confirmation number. So are cancellations.

What's possible depends on the airline's fare rules. Fares are generally non-refundable, and some allow an airline flight credit. Only the airline can tell you what your fare allows.

If the airline tells you to contact the travel agency or company you booked with, that's us. [The airline told me to contact the third party](/help/confirmation-and-travel/airline-says-contact-third-party) explains what to do.

## On Defera Zero, can I still cancel for a refund?

Yes. On Defera Zero, you can still take 75% of your payments back to your card, up to 24 hours before departure. You do it from your trip in Defera:

1. **Open the cancel screen.** Open your trip in **Trips**, tap **Flight Options**, then **Cancel**.
2. **Check the amount.** Tap **Cancel & receive 75% refund**. The screen shows the exact amount before you confirm.
3. **Confirm.** The refund then runs automatically.

Once the refund is sent, your bank decides when it shows up on your card, typically within 5–10 business days. [What you get back when you cancel](/help/changes-cancellations/what-you-get-back) has the details.

## What can I still do in Defera?

- **Add seats or checked bags.** From **Flight Options** on your trip, tap **Purchase Seats** or **Purchase Baggage**. This works whether your plan is still running or paid in full.
- **Download your Trip Statement.** It shows your airline confirmation number once your trip is paid in full. Tap the download icon in your trip's header row.
- **Get help with anything on your trip.** [Chat with the Defera Concierge](/support), our AI concierge, or [contact us](/contact).`,
  },
  {
    slug: 'airline-app-shows-nothing',
    title: 'Why doesn’t the airline’s app show my trip yet?',
    summary: 'The airline’s app finds trips by confirmation number, and yours is released once your trip is paid in full. Until then, your trip is in Trips, and that’s normal.',
    keywords: ['can’t find booking', 'airline website', 'reservation not found', 'booking not showing', 'manage trip', 'operated by', 'codeshare'],
    related: [
      'confirmation-and-travel/when-confirmation-number',
      'confirmation-and-travel/use-confirmation-with-airline',
      'booking/codeshare-flights',
      'confirmation-and-travel/airline-says-contact-third-party',
    ],
    updated: '2026-10-05',
    body: `The airline's app needs your airline confirmation number to find your trip, and your confirmation number is released once your trip is paid in full. Until then, your trip and its payment schedule live in **Trips** in Defera.

We know it can be unsettling to search the airline's app and find nothing. If you're still paying off your trip, that's completely normal, and nothing is wrong with your booking.

## Why does the airline need my confirmation number?

Airline apps and websites look up a reservation by its confirmation number, usually together with the traveler's last name. Your confirmation number is the code the airline uses to find your reservation, and it's released to your trip page once your trip is paid in full.

Your Defera booking reference won't work here. It's the code at the top of your booking confirmation email, and it's how we find your trip at Defera, but the airline can't look it up.

## Where can I see my trip in the meantime?

While you're paying off your trip, here's where to find its details:

- **Your trip page.** Open **Trips** and tap your trip to see your flights and your payment schedule.
- **Your booking confirmation email.** It has a card for each flight, with its times, airports, airline and flight number. Its **Add to calendar** button adds every flight to your calendar.
- **Your Trip Statement.** It's a print-ready PDF of your booking. Download it with the download icon in your trip's header row.

If you'd like your confirmation number sooner, you can pay off your remaining balance early. There's no penalty for paying early.

## Paid in full but no confirmation number yet?

When your final payment goes through, a release request is sent to our team for you automatically. Our team releases confirmation numbers Monday through Friday, Pacific time:

- **Monday through Thursday, or Friday morning (Pacific time):** expect your confirmation number within 24 hours.
- **Friday afternoon or over the weekend:** expect it on Monday, or Tuesday at the latest.

[I've paid off my trip. Where's my confirmation number?](/help/confirmation-and-travel/paid-off-no-confirmation) explains what to do if it's taking longer.

## Have your confirmation number, but the airline can't find your trip?

Check these:

- **Try the airline that operates the flight.** If a flight shows "Operated by" another airline, a partner airline flies it, and that airline's app or website is usually where your trip lives.
- **Search with the right details.** Use your confirmation number and the traveler's last name, which is what most airlines ask for.
- **Use each code with its own airline.** If your confirmation number shows two codes separated by a slash, the first code is for the first airline you fly. The next is for the airline that operates your following flight or your return.
- **Use the airline's code, not ours.** Your Defera booking reference won't work with the airline.

## Still can't find it?

[Chat with the Defera Concierge](/support), our AI concierge. It can check on your trip and send a request to our team. Include your Defera booking reference, the airline, and what you've tried. You can also [contact us](/contact).`,
  },
  {
    slug: 'check-in',
    title: 'Checking in for your flight',
    summary: 'You check in with the airline as usual, using your airline confirmation number. You’ll find it on your trip page in Trips once your trip is paid in full.',
    keywords: ['online check-in', 'boarding pass', 'airport', 'travel day', 'operating airline', 'seat assignment', 'departure'],
    related: [
      'confirmation-and-travel/use-confirmation-with-airline',
      'confirmation-and-travel/wheelchair-assistance',
      'seats-and-bags/sitting-together',
      'booking/codeshare-flights',
    ],
    updated: '2026-10-05',
    body: `You check in with the airline as usual, using your airline confirmation number. It's released once your trip is paid in full, and you'll find it on your trip page in **Trips**.

Checking in is how you confirm with the airline that you're flying, and it's how you get your boarding pass. Each airline sets its own check-in times, so look at its app or website before your travel day to see when check-in opens.

## How do I check in?

Here's what to do:

1. **Find your confirmation number.** Open **Trips** and tap your trip. Your confirmation number is on your trip page.
2. **Check which airline flies each flight.** If a flight shows "Operated by" another airline, check in with that airline. The check-in desk belongs to the airline that operates the flight.
3. **Match each code to its airline.** If your confirmation number shows two codes separated by a slash, use each code with its own airline, in the order you fly.
4. **Check in with the airline.** Use the airline's app, website or airport counter. Most airlines ask for your confirmation number and the traveler's last name.

## What should I check before I go?

A few things are worth checking before your travel day:

- **Everyone's ID.** Every traveler needs ID that matches the name on your booking. International trips need valid travel documents for every traveler, including children and infants. [Passports, IDs and matching names](/help/confirmation-and-travel/travel-documents) has the details.
- **Your seats.** If your fare doesn't allow advance seat selection, the airline assigns seats at check-in. That's the airline's fare rule, not a problem with your booking.
- **Wheelchair assistance.** If a traveler needs it, open your trip and check that their answers are complete in the Wheelchair assistance box under their name, in the Passengers section.
- **A complimentary upgrade.** If we've arranged a complimentary cabin upgrade for your trip, you'll have a card on your trip page and an email about it. The upgrade shows on your boarding pass at check-in. There's nothing you need to do, it doesn't cost anything, and it doesn't change your fare or your payment plan.

## What if the airline can't find my trip?

Try the airline that operates your flight, and make sure you're using your airline confirmation number, not your Defera booking reference. [Why doesn't the airline's app show my trip yet?](/help/confirmation-and-travel/airline-app-shows-nothing) has more checks.

## What if my flight is soon and my confirmation number hasn't arrived?

[Chat with the Defera Concierge](/support), our AI concierge, and tell it when you fly. It can check on your trip and send a request to our team. [Getting urgent help before your flight](/help/contact/urgent-help-before-a-flight) explains how to reach us when time is short.

## What if the airline tells me to contact the company I booked with?

That's us. [Chat with the Defera Concierge](/support), our AI concierge, which can send a request to our team, or [contact us](/contact).`,
  },
  {
    slug: 'travel-emails-from-airline',
    title: 'Which emails come from the airline?',
    summary: 'Defera emails you from the moment you book. The airline starts emailing you directly once your trip is paid in full and your confirmation number is released.',
    keywords: ['airline email', 'who sent this', 'Defera email', 'itinerary email', 'schedule change email', 'booking reference', 'confirmation email'],
    related: [
      'emails/emails-we-send',
      'emails/is-this-email-from-defera',
      'emails/booking-confirmation-email',
      'emails/paid-off-email',
    ],
    updated: '2026-10-05',
    body: `The airline emails you directly once your trip is paid in full and your confirmation number is released. Until then, the emails about your booking come from Defera, starting with your booking confirmation email the moment you book.

So if you're still paying off your trip and haven't heard from the airline yet, that's normal, and nothing is wrong with your booking.

## Which emails come from Defera?

Here are the main ones:

- **Your booking confirmation email.** It comes from bookings@mail.defera.app the moment you book. Its subject line starts with your Defera booking reference, followed by "You're going to" and your destination city.
- **The "Your trip has been paid off!" email.** It also comes from bookings@mail.defera.app, when our team releases your confirmation number. Tap **Get Confirmation Number** in it to see your number.
- **Answers to your requests.** When our team answers a request you've sent, the answer comes by email, and it also shows on your trip page.
- **Changes we make for you.** We email you if we move you to a faster route under the Free Flight Upgrade, or if we arrange a complimentary cabin upgrade for you.
- **A check-in before your trip.** You may get an email asking whether you're still planning to take your trip. It's a courtesy check-in, not a warning, and if you're still going, there's nothing you need to do. [The "Will you still be taking this trip?" email](/help/changes-cancellations/still-taking-this-trip) explains it.

## What will the airline send me?

Once your confirmation number is released, the airline emails you directly, at the email address on your booking. From then on, the airline may also email you about your flights.

If you get an email from an airline about a schedule change or a cancelled flight, contact us. You didn't do anything wrong, and we'll help you navigate it. [If the airline changes or cancels your flight](/help/changes-cancellations/airline-schedule-change) explains what to do.

## Which code is which?

The code at the top of your booking confirmation email is your Defera booking reference. It's how we find your trip at Defera, but it isn't the airline confirmation number.

Your airline confirmation number is the code the airline uses to find your reservation. It appears on your trip page once it's released, and the **Get Confirmation Number** button in your "Your trip has been paid off!" email opens it.

## Can't find an email?

Everything in your booking confirmation email is also on your trip page: open **Trips** and tap your trip. Your confirmation number appears there too, once it's released.

It's also worth checking your spam or junk folder, and searching your inbox for bookings@mail.defera.app. [I didn't get an email from Defera](/help/emails/missing-email) has more help.`,
  },
  {
    slug: 'airline-says-contact-third-party',
    title: 'The airline told me to contact the third party',
    summary: 'You booked through Defera, so when the airline points you to the travel agency or third party, it means us. Get in touch, and our team takes it from there.',
    keywords: ['travel agency', 'booking agent', 'booked through Defera', 'contact the agency', 'third-party booking', 'booking agency', 'referred back'],
    related: [
      'confirmation-and-travel/use-confirmation-with-airline',
      'contact/how-requests-work',
      'contact/how-to-contact',
      'changes-cancellations/airline-schedule-change',
    ],
    updated: '2026-10-23',
    body: `You booked your trip through Defera, so when an airline tells you to contact the travel agency or third party that made your booking, it means us. Contact us, and our team takes it from there.

We know it's frustrating to be sent from one company to another, especially when you're trying to sort out a trip. You're in the right place now.

## Why does the airline send me to you?

Airlines often refer customers back to the company they booked through. You might hear it called "the travel agency", "the third party" or "the company you booked with". For your trip, that's Defera, and helping with this is part of what we do.

It can come up when you ask the airline about changing or cancelling your flights. Once your confirmation number is released, those changes are normally made directly with the airline. If the airline sends you back to us instead, our team handles it.

## How do I reach you?

- **Chat.** [Chat with the Defera Concierge](/support), our AI concierge. It can send a request to our team.
- **Call.** Call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}) to reach the same concierge by voice, 24/7. Have your Defera booking reference (or your account email) and the traveler's name ready, so it can confirm it's your trip before sharing any details.
- **Message us.** Send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.

## What should I include?

Sharing everything up front saves back-and-forth. Include:

- your Defera booking reference, the code at the top of your booking confirmation email
- the traveler's name, and the airline, flight number and date
- your airline confirmation number, if it's been released
- what the airline told you, and what you need

## What happens next?

When the Defera Concierge sends a request to our team, here's what happens:

1. **Your request shows on your trip page.** It appears as a card, marked "Submitted · in progress" while our team works on it.
2. **Our team answers.** The answer appears on that card.
3. **You get it by email too.** The same answer comes to you by email, and the card stays on your trip page until you dismiss it.

## What if the airline changed or cancelled my flight?

You didn't do anything wrong, and we'll help you navigate it. Tell us what the airline said, including anything in its emails. [If the airline changes or cancels your flight](/help/changes-cancellations/airline-schedule-change) explains what to expect.`,
  },
  {
    slug: 'proof-of-onward-travel',
    title: 'Proof of onward travel',
    summary: 'Download your Trip Statement from your trip page. It shows your full itinerary and passenger names. Whether it’s accepted is up to whoever is asking for it.',
    keywords: ['visa application', 'immigration', 'itinerary PDF', 'return flight proof', 'border check', 'travel itinerary', 'Trip Statement'],
    related: [
      'payments/trip-statement',
      'confirmation-and-travel/travel-documents',
      'confirmation-and-travel/when-confirmation-number',
    ],
    updated: '2026-10-05',
    body: `If you need to show your upcoming travel, for example for a visa application or an onward-travel check, download your Trip Statement. It's a print-ready PDF of your booking, with your full flight itinerary and passenger names.

Before your confirmation number is released, it's the strongest document we can give you to show your travel plans. Whether a particular airline, border authority or consulate accepts it is up to them, so check what they require before you travel.

## What is proof of onward travel?

Some countries, and some airlines flying there, ask travelers to show they have a flight out, such as a return flight or a flight on to another country. This is often called proof of onward travel. Visa applications sometimes ask to see your travel plans too.

## How do I download my Trip Statement?

Here's how:

1. **Open your trip.** Open **Trips** and tap your trip.
2. **Tap the download icon.** It's in the trip's header row, next to the message and call buttons.
3. **Save or share the PDF.** The file is named after your booking reference.

Every trip has a Trip Statement, whatever its status.

## What's on it?

- **Your full flight itinerary:** airlines, flight numbers, and dates and times for every flight
- **Passenger names** for everyone on the booking
- **Your pricing summary and payment plan,** with its history of paid, upcoming and any past-due payments
- **Your reference code:** your Defera booking reference until your trip is paid in full, then your airline confirmation number

It's a booking statement, not a boarding pass. You'll get your boarding pass from the airline when you check in.

## What if I'm asked for an airline confirmation number?

Your airline confirmation number is released once your trip is paid in full. Until then, your Trip Statement shows your Defera booking reference instead.

If you need your confirmation number by a certain date, paying off your remaining balance early gets it to you sooner, and there's no penalty for paying early. How long it takes then depends on when your final payment goes through:

- **Monday through Thursday, or Friday morning (Pacific time):** expect your confirmation number within 24 hours.
- **Friday afternoon or over the weekend:** expect it on Monday, or Tuesday at the latest.

[When do I get my confirmation number?](/help/confirmation-and-travel/when-confirmation-number) explains how it works.

## Can you send it to someone else?

We can only send trip documents to the email address on your account. But the PDF is yours to share: you can forward or print your Trip Statement yourself, for a visa application, an employer or a travel companion.

## What else should I check?

Entry, visa and onward-travel rules are set by governments and airlines, and they change. Check official sources before you travel: the US State Department, the CDC, your destination's embassy and your airline. [Passports, IDs and matching names](/help/confirmation-and-travel/travel-documents) has more.

Questions about your Trip Statement? Ask the [Defera Concierge](/support), our AI concierge.`,
  },
  {
    slug: 'wheelchair-assistance',
    title: 'Requesting wheelchair assistance',
    summary: 'Add a special request that mentions a wheelchair when you book, then answer the airline’s four questions. You can update the answers later on your trip page.',
    keywords: ['mobility aid', 'special assistance', 'disability', 'accessibility', 'special request', 'scooter', 'reduced mobility', 'airport assistance'],
    related: [
      'booking/traveler-information',
      'confirmation-and-travel/check-in',
      'account/saved-passengers',
    ],
    updated: '2026-10-05',
    body: `To request wheelchair assistance, add a special request that mentions a wheelchair for that traveler when you book. Checkout then asks the airline's four assistance questions for that traveler.

After you book, the answers are saved on your trip, and you can change them there at any time while your trip is active.

## How do I request it at checkout?

Here's what happens:

1. **You add a special request.** When you enter the traveler's details at checkout, add a special request that mentions a wheelchair. Special requests are optional, and they're also where you can note things like meals.
2. **Checkout asks four questions.** When a traveler's special request mentions a wheelchair, checkout asks the airline's four assistance questions for that traveler.
3. **You answer all four.** Each one is a Yes or No, and all four are required before checkout continues.
4. **Your answers are saved on your trip.** After you book, you'll find them on your trip page, under that traveler.

## What are the four questions?

Here are the four questions checkout asks for the traveler:

- Can they walk from the ticket counter to the aircraft door?
- Do they need help on stairs while boarding?
- Do they need to be lifted and transferred onto their seat?
- Are they traveling with their own wheelchair?

All four are required because the airline can't arrange assistance without them. If you're booking for someone else and aren't sure of an answer, check with them before you finish checkout.

## Can I change the answers after booking?

Yes. Needs can change, and you can update the answers at any time while your trip is active:

1. Open **Trips** and tap your trip.
2. In the Passengers section, find the Wheelchair assistance box under the traveler's name.
3. Update the answers.

Our team is notified whenever you change them.

## What does "Needs answers" mean?

If a trip was booked with a wheelchair request but without answers, the Wheelchair assistance box is marked "Needs answers". Fill in the answers there, so the airline has what it needs to arrange assistance.

## What if I'm traveling with my own wheelchair or scooter?

Non-collapsible wheelchairs, scooters and other battery-powered wheelchairs can be checked at the airline's check-in counter or at the departure gate. Airlines have their own rules for some lithium-ion batteries, so check with your airline before you travel.

## Didn't add it when you booked?

If a traveler needs wheelchair assistance and you didn't request it at checkout, [chat with the Defera Concierge](/support), our AI concierge. It can send a request to our team. Tell it which traveler needs assistance and on which flights. You can also [contact us](/contact).`,
  },
  {
    slug: 'travel-documents',
    title: 'Passports, IDs and matching names',
    summary: 'Each traveler’s name must match the government ID they’ll travel with, and international trips need passport details. Check entry rules with official sources.',
    keywords: ['legal name', 'passport number', 'visa', 'government ID', 'name mismatch', 'international travel', 'entry requirements', 'date of birth'],
    related: [
      'booking/traveler-information',
      'changes-cancellations/correct-traveler-details',
      'account/saved-passengers',
      'confirmation-and-travel/proof-of-onward-travel',
    ],
    updated: '2026-11-02',
    body: `Each traveler's name on your booking must be their full legal name, matching the government ID they'll travel with. International trips also need passport details, and every traveler on an international trip, including children and infants, needs valid travel documents.

Getting names right matters, because airlines and airport security check them against each traveler's ID. Entry rules, such as visas, are set by governments and airlines, so check official sources for your destination.

## What do you ask for when I book?

Here's what checkout asks for:

- **For every traveler:** full legal name matching their government ID, date of birth and gender.
- **For at least one traveler:** contact information.
- **For international trips:** nationality, the country that issued the passport, passport number and expiration date. These are required before payment.
- **Optional:** frequent flyer numbers, a Known Traveler Number (for TSA PreCheck or Global Entry), and special requests such as meals or a wheelchair.

## How do I check the details on my booking?

It's worth checking every name and date of birth against each traveler's ID after you book. You can see them in two places:

- **Your booking confirmation email.** It lists every traveler detail entered at checkout. Passport, ID and Known Traveler numbers show only their last 4 digits.
- **Your trip page.** The full numbers are on your trip's Flight Details screen: open **Trips** and tap your trip.

## How do I book faster next time?

Save each traveler to the Passengers section of your profile, with their name exactly as it appears on their ID. Open your profile from the avatar menu, then tap **Add** at the top right of the Passengers section.

Next time you book, you can pick saved travelers from a list, and their details fill in for you.

## What if a name or date of birth is wrong?

[Chat with the Defera Concierge](/support), our AI concierge. It can send a request to our team. Include:

- the name or date of birth as it is on your booking
- the name or date of birth as it appears on the traveler's ID

The request shows as a card on your trip page, and the answer arrives there and by email. [Fixing a name or date of birth on your booking](/help/changes-cancellations/correct-traveler-details) has more.

## What about visas and entry rules?

Each traveler is responsible for checking visa, vaccination and entry rules. They're set by governments and airlines, and they change, so confirm them with official sources: the US State Department, the CDC, your destination's embassy and your airline.

To help, checkout flags it when a traveler may need a visa, an e-visa or an electronic travel authorization (such as the US ESTA or UK ETA) for a destination or connection, based on their nationality. It also warns when a passport expires within 6 months of the trip, which many countries don't allow. A day after you book an international trip, we email you a short list of anything to take care of. These reminders come from public data and aren't a guarantee, so always confirm with the official sources.

For US airport security screening, see the [TSA](https://www.tsa.gov).`,
  },
  {
    slug: 'layovers-and-connections',
    title: 'Layovers and connecting flights',
    summary: 'Every connecting flight is listed in your booking confirmation email, on your trip page and in your Trip Statement. Partner flights show as “Operated by” that airline.',
    keywords: ['stopover', 'connection', 'layover time', 'second flight', 'operated by', 'two airlines', 'nonstop', 'stops'],
    related: [
      'booking/codeshare-flights',
      'booking/free-flight-upgrade-question',
      'booking/only-connecting-flights',
      'changes-cancellations/airline-schedule-change',
    ],
    updated: '2026-10-05',
    body: `Every flight on your trip, including each connection, is listed in your booking confirmation email and on your Trip Statement, with its airline, flight number and times. If a connecting flight is flown by a partner airline, your itinerary shows "Operated by" and that airline's name.

A layover is the time between landing on one flight and taking off on the next. It's a normal part of many trips, and you can see each of yours before you travel.

## Where can I see my connections?

- **Your booking confirmation email.** It has a card for each flight, with its times, airports, airline, flight number and aircraft.
- **Your trip page.** Open **Trips** and tap your trip to see its flight details.
- **Your Trip Statement.** It lists the airline, flight number, date and times of every flight. Download it with the download icon in your trip's header row.

## What if my connection is on another airline?

Sometimes a connecting flight is flown by a partner airline. Your itinerary shows "Operated by" and that airline's name. Here's what that means for you:

- **Check-in:** the operating airline runs check-in for that flight.
- **Bags and seats:** the operating airline usually sets the baggage rules and seat map.
- **Your confirmation number:** it may show two codes separated by a slash, one for each airline, in the order you fly. Use each code with its own airline.

It's completely normal, and it doesn't mean anything is wrong with your booking. [What is a codeshare flight?](/help/booking/codeshare-flights) explains more.

## Can I get a shorter layover?

At checkout, you're asked whether we may move you to a faster route, with a shorter total trip time or a shorter layover. If you say yes, you also choose whether we may move you to any airline or only the airline you booked. Saying no is a perfectly normal answer too.

If you said yes, here's what we promise:

- **A real improvement.** We only move you for a shorter total trip time or a shorter layover.
- **The same cabin.** It's a faster route, not a cabin upgrade.
- **The same travel dates,** departing near your original time.
- **No extra cost.** If a faster option costs more, you don't pay the difference.
- **An email if we make a change.** We never move you without telling you.

Saying yes doesn't mean your flight will change: it's permission to look, not a plan to move you. To change your answer either way, ask the [Defera Concierge](/support), which can send it to our team.

## What if a delay affects my connection?

Delays and cancellations are handled by the airline operating the flight, under the rules that apply. If one affects your connection, you didn't do anything wrong. Chat with the Defera Concierge or contact us, and we'll help you navigate it. [If the airline changes or cancels your flight](/help/changes-cancellations/airline-schedule-change) has more.

## How do I find flights with fewer stops next time?

When you search, try nearby dates or times. They often bring up different routings. [Why am I only seeing flights with stops?](/help/booking/only-connecting-flights) has more search tips.

## What if I connect through another country?

Entry and visa rules are set by governments and airlines, so check the rules for every country on your itinerary, including the ones you connect through, with official sources. [Passports, IDs and matching names](/help/confirmation-and-travel/travel-documents) has more.`,
  },
];
